import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import debounce from 'lodash/debounce';
import Select, { createFilter } from 'react-select';
import Tooltip from '@material-ui/core/Tooltip';
import { useTranslation } from 'react-i18next';
import { confirmAlert } from 'react-confirm-alert';
import { useRootStore } from '../../../../mobx/bridge';
import styles from './Settings.module.css';
import commonStyles from '../../../../assets/stylesheets/common.module.css';
import * as utils from '../../../../helpers/utils';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import i18n from '../../i18n';
import { formatAutoReplyExpiration } from '../../../../helpers/dateHelpers';

const stringify = (option) => option.label;
const filterOption = createFilter({ stringify });

const options = {
  month: 'long',
  day: 'numeric',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

const snoozeTimePeriods = [];
const startDate = new Date();
const optionsCount = 47;
const minutesStep = 30;
startDate.setHours(0, 30, 0, 0);

for (let i = 0; i < optionsCount; i += 1) {
  const hours = startDate.getHours();
  const minutes = startDate.getMinutes();

  const formattedDate = [hours, String(minutes).padStart(2, '0')].join(':');
  const minutesPart = minutes === 30 ? '.5' : '';
  const ending =
    (hours === 0 && minutes === 30) || (hours === 1 && minutes === 0)
      ? i18n.t('AutoReplyCustomAwayMessageExpirationDropdown.Hour')
      : i18n.t('AutoReplyCustomAwayMessageExpirationDropdown.Hours');

  const label = `${hours}${minutesPart} ${ending}`;

  snoozeTimePeriods.push({
    value: formattedDate,
    label,
  });
  startDate.setMinutes(startDate.getMinutes() + minutesStep);
}

const AfterHoursAutoReply = (props) => {
  const { title, enabledAutoreplies } = props;

  const { t } = useTranslation();

  return (
    <div className={[styles.settingsSeparator, styles.settingsSeparatorColumn].join(' ')}>
      <span className={styles.autoRepliesTitleText}>{title}</span>
      <div>
        {enabledAutoreplies?.length > 0 ? (
          <span className={[styles.autoReplyStatus, styles.autoReplyStatusEnabled].join(' ')}>
            {t('Common.On')}:{' '}
            {enabledAutoreplies
              .map((autoReply) => autoReply.Title)
              ?.join(', ')
              ?.trim()}
          </span>
        ) : (
          <span className={styles.autoReplyStatus}>{t('Common.Off')}</span>
        )}
      </div>
    </div>
  );
};

const AwayMessageAutoReply = (props) => {
  const { enabled, title, autoReplyTitle, expiration } = props;

  const { t } = useTranslation();

  return (
    <div className={[styles.settingsSeparator, styles.settingsSeparatorColumn].join(' ')}>
      <span className={styles.autoRepliesTitleText}>{title}</span>
      <div>
        {enabled ? (
          <span className={[styles.autoReplyStatus, styles.autoReplyStatusEnabled].join(' ')}>
            {t('Common.On')}: {autoReplyTitle} (
            {expiration
              ? t('AwayMessageAutoReply.MessageExpiresAt', {
                  expiration: formatAutoReplyExpiration(expiration),
                })
              : t('AwayMessageAutoReply.MessageHasNoExpiration')}
            )
          </span>
        ) : (
          <span className={styles.autoReplyStatus}>{t('Common.Off')}</span>
        )}
      </div>
    </div>
  );
};

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const Settings = observer(function Settings() {
  const { t } = useTranslation();

  const channelFilterPeriods = React.useRef([]);
  const filterChannelsCheckbox = React.useRef(null);

  const {
    commonStore,
    settingsStore,
    autoReplyV2Store,
    sendingEvents,
    workTimeStore,
  } = useRootStore();

  const settings = settingsStore.getLoggedUserSettings;
  const [snoozeSelectedOption, setSnoozeSelectedOption] = React.useState(snoozeTimePeriods[0]);
  const [channelFilterSelectedOption, setChannelFilterSelectedOption] = React.useState(null);

  React.useEffect(() => {
    channelFilterPeriods.current.push({ value: `1`, label: `1 ${t('Settings.Month')}` });
    for (let i = 2; i <= 12; i += 1) {
      channelFilterPeriods.current.push({ value: `${i}`, label: `${i} ${t('Settings.Months')}` });
    }

    if (settings.FilterRecentMessagesFrom) {
      setChannelFilterSelectedOption(
        channelFilterPeriods.current.find(
          (option) => option.value === settings.FilterRecentMessagesFrom
        )
      );
    } else {
      setChannelFilterSelectedOption(channelFilterPeriods.current[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function closeSettings() {
    commonStore.setToggleSettingsMenu();
  }

  function logOut() {
    commonStore.setLogoutStatus(true);
  }

  const handleChangeSnoozePeriod = debounce((option) => {
    setSnoozeSelectedOption(option);
  }, 250);

  const toggleMuteChatSounds = debounce(() => {
    sendingEvents.socketEmitToggleMuteChatSounds();
  }, 250);

  const toggleIsWebchatEnabled = debounce(() => {
    sendingEvents.socketEmitToggleIsWebchatEnabled();
  }, 250);

  const toggleIsAutoCloseWebChatEnabled = debounce((autoCloseWebChatTimeInHours) => {
    commonStore.setIsAutoCloseWebChatEnabled(!!autoCloseWebChatTimeInHours);
    sendingEvents.socketEmitToggleIsAutoCloseWebChatEnabled(autoCloseWebChatTimeInHours);
  }, 250);

  const togglePersistentNotifications = debounce(() => {
    sendingEvents.socketEmitTogglePersistentNotifications();
  }, 250);

  const toggleShowHiddenMessages = debounce(() => {
    commonStore.setSpecificScrollHeight(commonStore.messagesContainerRef.current.scrollTop);
    sendingEvents.socketEmitToggleShowHiddenMessages();

    if (settings?.SelectedChannel) {
      sendingEvents.socketEmitRoom(
        settings?.SelectedChannel,
        utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
      );
    }
  }, 250);

  const snoozeNotifications = debounce(() => {
    sendingEvents.socketEmitSnoozeNotificationsUntil(
      snoozeSelectedOption ? snoozeSelectedOption.value : snoozeTimePeriods[0].value
    );
  }, 250);

  const toggleChannelFilterPeriod = debounce(() => {
    if (!settings.FilterRecentMessagesFrom && channelFilterSelectedOption) {
      sendingEvents.socketEmitChangeChannelFilterPeriod(channelFilterSelectedOption.value);
    } else {
      sendingEvents.socketEmitChangeChannelFilterPeriod(null);
    }
  }, 250);

  const handleChannelFilterDropdown = debounce((option) => {
    setChannelFilterSelectedOption(option);

    if (settings?.FilterRecentMessagesFrom && option.value !== settings.FilterRecentMessagesFrom) {
      sendingEvents.socketEmitChangeChannelFilterPeriod(option.value);
    }
  }, 250);

  const confirmToggleIsWebchatEnabled = () => {
    if (!commonStore.isWebchatEnabled && !commonStore.clientWebsiteUrl) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <CustomConfirmAlert
            title={t('Settings.NoWebSiteAvailableTitle')}
            message={t('Settings.NoWebsiteAvailable')}
            trueText={t('Common.Confirm')}
            falseText={t('Common.Close')}
            falseEvent={onClose}
          />
        ),
      });
    } else {
      const webChatEnabledMessage = commonStore.isWebchatEnabled
        ? t('Settings.ConfirmDisableWebChat')
        : t('Settings.ConfirmEnableWebChat', {
            websiteUrl: commonStore.clientWebsiteUrl,
          });

      confirmAlert({
        customUI: ({ onClose }) => (
          <CustomConfirmAlert
            title={t('Common.AreYouSure')}
            message={webChatEnabledMessage}
            trueText={t('Common.Confirm')}
            trueEvent={toggleIsWebchatEnabled}
            falseText={t('Common.Close')}
            falseEvent={onClose}
          />
        ),
      });
    }
  };

  const confirmToggleIsAutoCloseWebChatEnabled = () => {
    const defaultAutoCloseWebChatTimeInHours = 24;
    if (commonStore.isAutoCloseWebChatEnabled) {
      toggleIsAutoCloseWebChatEnabled();
    } else {
      confirmAlert({
        customUI: ({ onClose }) => (
          <CustomConfirmAlert
            title={t('Common.AreYouSure')}
            message={t('Settings.ConfirmEnableAutoCloseWebChat')}
            trueText={t('Common.Confirm')}
            trueEvent={() => toggleIsAutoCloseWebChatEnabled(defaultAutoCloseWebChatTimeInHours)}
            falseText={t('Common.Close')}
            falseEvent={onClose}
          />
        ),
      });
    }
  };

  let loggedUsername = t('Common.LoadingWithThreeDots');
  if (commonStore.getLoggedUser) {
    const { FirstName, LastName, CpUsername } = commonStore.getLoggedUser;
    loggedUsername = !FirstName && !LastName ? CpUsername : [FirstName, LastName].join(' ');
  }

  const initials = commonStore.getLoggedUser?.getInitials || '??';

  const renderMuteSoundButton = () => (
    <Tooltip
      PopperProps={{ disablePortal: true }}
      arrow
      enterDelay={1000}
      title={settings.MuteSounds ? t('Toolbar.UnmuteSound') : t('Toolbar.MuteSound')}
    >
      <button
        type="button"
        className={[
          styles.settingsBtn,
          settings.MuteSounds ? styles.mutedBtn : styles.unmutedBtn,
        ].join(' ')}
        onClick={toggleMuteChatSounds}
      />
    </Tooltip>
  );

  const toggleAutoRepliesPopup = () => {
    commonStore.setToggleIsManageAutoRepliesPopupOpen();
  };

  return (
    <Grid component="div" className={styles.settingsContainer} data-testid="settingsContainer">
      <Grid component="div" container item xs={12} alignItems="center" alignContent="flex-start">
        <span className={styles.chatSettingsHeader}>{t('Settings.ChatSettings')}</span>

        <div className={styles.buttonsContainer}>
          {renderMuteSoundButton()}

          <button
            type="button"
            className={[styles.settingsBtn, styles.closeBtn].join(' ')}
            onClick={closeSettings}
            data-testid="settingsCloseBtn"
          />
        </div>
      </Grid>

      <Grid component="div" container item xs={12} alignItems="center" alignContent="flex-start">
        <div className={styles.settingsSeparator}>
          <span className={styles.settingsSeparatorText}>{t('Settings.LoggedInAs')}</span>
        </div>

        <div className={styles.loggedUserContainer}>
          <div className={styles.nameContainer}>
            <span style={utils.getColorFromName(initials)} className={styles.userInitials}>
              {initials}
            </span>

            <span className={styles.loggedUser}>{loggedUsername}</span>
          </div>

          {!commonStore.cpHeaderHeight && !utils.inIframe() ? (
            <div className={styles.btnContainer}>
              <button
                type="button"
                className={styles.blueTextBtn}
                onClick={logOut}
                data-testid="logoutBtn"
              >
                {t('Common.LogOut')}
              </button>
            </div>
          ) : null}
        </div>
      </Grid>

      {commonStore.getHasAccessToWebChats ? (
        <Grid
          component="div"
          container
          item
          xs={12}
          alignItems="flex-start"
          alignContent="center"
          direction="column"
        >
          <div className={styles.settingsSeparator}>
            <span className={styles.settingsSeparatorText}>{t('Settings.WebChatHeader')}</span>

            <Tooltip
              PopperProps={{ disablePortal: true }}
              arrow
              enterDelay={1000}
              title={
                commonStore?.isWebchatEnabled
                  ? t('Settings.DisableWebChatTooltip')
                  : t('Settings.EnableWebChatTooltip')
              }
            >
              <div className={styles.helpIcon} />
            </Tooltip>
          </div>

          <label htmlFor="enableWebchat" className={styles.settingsCheckBoxContainer}>
            <input
              type="checkbox"
              name="enableWebchat"
              id="enableWebchat"
              onChange={confirmToggleIsWebchatEnabled}
              checked={commonStore.isWebchatEnabled}
              className={commonStyles.settingsCheckBox}
            />

            <span className={commonStyles.checkMark} />

            <span className={styles.settingsCheckboxLabel}>{t('Settings.EnableWebsiteChat')}</span>
          </label>

          <label htmlFor="autoCloseWebChat" className={styles.settingsCheckBoxContainer}>
            <input
              type="checkbox"
              name="autoCloseWebChat"
              id="autoCloseWebChat"
              onChange={confirmToggleIsAutoCloseWebChatEnabled}
              checked={commonStore.isAutoCloseWebChatEnabled}
              className={commonStyles.settingsCheckBox}
            />

            <span className={commonStyles.checkMark} />

            <span className={styles.settingsCheckboxLabel}>
              {t('Settings.EnableAutoCloseWebsiteChat')}
            </span>
          </label>
        </Grid>
      ) : null}

      <Grid
        component="div"
        container
        item
        xs={12}
        alignItems="flex-start"
        alignContent="center"
        direction="column"
      >
        <div className={styles.settingsSeparator}>
          <span className={styles.settingsSeparatorText}>{t('Settings.Notifications')}</span>
        </div>

        <div className={styles.snoozeNotificationsContainer}>
          <label htmlFor="snoozeNotifications">
            <input
              type="checkbox"
              name="snoozeNotifications"
              id="snoozeNotifications"
              onChange={snoozeNotifications}
              checked={settingsStore.getUserHasSnoozedNotifications || false}
              className={commonStyles.settingsCheckBox}
            />

            <span className={commonStyles.checkMark} />

            <span className={styles.settingsCheckboxLabel}>
              {settingsStore.getUserHasSnoozedNotifications
                ? t('Settings.SnoozeNotificationsUntil', {
                    snoozeNotificationsEndDate: new Date(
                      settings.SnoozeNotificationsUntil
                    ).toLocaleDateString('en-US', options),
                  })
                : t('Settings.SnoozeNotificationsTitle')}
            </span>
          </label>

          {settings && !settingsStore.getUserHasSnoozedNotifications ? (
            <Select
              className={styles.timeSelectDropdown}
              filterOption={filterOption}
              value={snoozeSelectedOption}
              onChange={handleChangeSnoozePeriod}
              options={snoozeTimePeriods}
              defaultValue={snoozeTimePeriods[0]}
            />
          ) : null}
        </div>

        <div className={styles.settingsCheckBoxContainer}>
          <label htmlFor="persistentNotifications">
            <input
              type="checkbox"
              name="persistentNotifications"
              id="persistentNotifications"
              onChange={togglePersistentNotifications}
              checked={(settings && settings.PersistentNotifications) || false}
              className={commonStyles.settingsCheckBox}
            />

            <span className={commonStyles.checkMark} />

            <span className={styles.settingsCheckboxLabel}>
              {settings && settings.PersistentNotifications
                ? t('Settings.PersistentNotificationsAdditionalInfo')
                : t('Settings.EnablePersitentNotifications')}
            </span>
          </label>

          <Tooltip
            PopperProps={{ disablePortal: true }}
            arrow
            enterDelay={1000}
            title={t('Settings.PersistentNotificationsInfoTitle')}
          >
            <div className={styles.helpIcon} />
          </Tooltip>
        </div>
      </Grid>

      <Grid component="div" container item xs={12} direction="column">
        <div className={styles.settingsSeparator}>
          <span className={styles.settingsSeparatorText}>{t('Settings.HiddenMessages')}</span>
        </div>

        <label htmlFor="showHiddenMessages" className={styles.settingsCheckBoxContainer}>
          <input
            type="checkbox"
            name="showHiddenMessages"
            id="showHiddenMessages"
            onChange={toggleShowHiddenMessages}
            checked={(settings && settings.ShowHiddenMessages) || false}
            className={commonStyles.settingsCheckBox}
          />

          <span className={commonStyles.checkMark} />

          <span className={styles.settingsCheckboxLabel}>
            {settings && settings.ShowHiddenMessages
              ? t('Settings.HiddenMessagesEnabled')
              : t('Settings.HiddenMessagesDisabled')}
          </span>
        </label>

        <div className={styles.snoozeNotificationsContainer}>
          <label htmlFor="filterChannels">
            <input
              ref={filterChannelsCheckbox}
              type="checkbox"
              name="filterChannels"
              id="filterChannels"
              onChange={toggleChannelFilterPeriod}
              checked={!!settings.FilterRecentMessagesFrom}
              className={commonStyles.settingsCheckBox}
            />

            <span className={commonStyles.checkMark} />

            <span className={styles.settingsCheckboxLabel}>{t('Settings.FilterOldChannels')}</span>
          </label>

          <Select
            className={styles.channelFilterDropdown}
            value={
              channelFilterPeriods.current[settingsStore.getChannelFilterDropdownIndex] ||
              channelFilterSelectedOption
            }
            onChange={handleChannelFilterDropdown}
            options={channelFilterPeriods.current}
          />
        </div>
      </Grid>

      <Grid component="div" container item xs={12} alignItems="center" alignContent="flex-start">
        <div className={styles.settingsSeparator}>
          <span className={styles.settingsSeparatorText}>
            {t('Settings.ConnectTrackingNumber')}
          </span>
        </div>

        <span className={styles.trackingNumber}>
          {commonStore.locationTrackingNumber && commonStore.locationTrackingNumber.Number
            ? commonStore.locationTrackingNumber.Number
            : t('Settings.NoTrackingNumberForLocation')}
        </span>
      </Grid>

      <Grid component="div" container item xs={12} alignItems="center" alignContent="flex-start">
        <div className={styles.settingsSeparator}>
          <span className={styles.settingsSeparatorText}>{t('Settings.AutoRepliesTitle')}</span>

          <button
            type="button"
            onClick={toggleAutoRepliesPopup}
            className={styles.manageAutoRepliesBtn}
          >
            {t('Settings.ManageAutoReplies')}
          </button>
        </div>

        <div className={styles.autoRepliesSeparator}>
          {t('Settings.WorkingHoursAutoReplyHeader')}

          <Tooltip
            PopperProps={{ disablePortal: true }}
            arrow
            enterDelay={1000}
            title={
              <>
                <span>{t('Settings.WorkingHoursAutoReplyDescription')}</span>

                <div className={styles.toolTipSeparator}>
                  {workTimeStore.getFormattedWorkTime.map((m) => (
                    <div key={m}>{m}</div>
                  ))}
                </div>
              </>
            }
          >
            <div className={styles.helpIcon} />
          </Tooltip>
        </div>

        {commonStore.getHasAccessToTextChats ? (
          <AfterHoursAutoReply
            title="Text chat"
            enabledAutoreplies={autoReplyV2Store.getActiveTextScheduledAutoReply}
          />
        ) : null}

        {commonStore.getHasAccessToWebChats && commonStore?.isWebchatEnabled ? (
          <AfterHoursAutoReply
            title="Web chat"
            enabledAutoreplies={autoReplyV2Store.getActiveWebScheduledAutoReply}
          />
        ) : null}

        <div className={styles.autoRepliesSeparator}>
          {t('Settings.AutoReplyHeader')}

          <Tooltip
            PopperProps={{ disablePortal: true }}
            arrow
            enterDelay={1000}
            title={t('Settings.AutoReplyDescription')}
          >
            <div className={styles.helpIcon} />
          </Tooltip>
        </div>

        {commonStore.getHasAccessToTextChats ? (
          <AwayMessageAutoReply
            title="Text Chat"
            enabled={autoReplyV2Store.getActiveTextCustomAwayMessageAutoReply}
            autoReplyTitle={autoReplyV2Store.getActiveTextCustomAwayMessageAutoReply?.Title}
            expiration={autoReplyV2Store.getActiveTextCustomAwayMessageAutoReply?.ExpirationTime}
          />
        ) : null}

        {commonStore.getHasAccessToWebChats && commonStore?.isWebchatEnabled ? (
          <AwayMessageAutoReply
            title="Web Chat"
            enabled={autoReplyV2Store.getActiveWebCustomAwayMessageAutoReply}
            autoReplyTitle={autoReplyV2Store.getActiveWebCustomAwayMessageAutoReply?.Title}
            expiration={autoReplyV2Store.getActiveWebCustomAwayMessageAutoReply?.ExpirationTime}
          />
        ) : null}
      </Grid>
    </Grid>
  );
});
Settings.whyDidYouRender = true;
Settings.displayName = 'Settings';

export default Settings;
